/**
 * Bilingual fallbacks for HTTP / ApiService error paths.
 * Kept out of TranslateService on purpose: TranslateService injects HttpClient,
 * so using it inside HttpInterceptor or ApiService (called during HttpClient
 * bootstrap) causes a circular DI dependency and a white screen.
 */
const MESSAGES: Record<string, { en: string; ar: string }> = {
  'errors.network': {
    en: "Can't reach the server. Please check your connection and try again.",
    ar: 'تعذّر الاتصال بالخادم. يُرجى التحقّق من الإنترنت والمحاولة مجدّدًا.',
  },
  'errors.sessionExpired': {
    en: 'Your session has expired. Please sign in again.',
    ar: 'انتهت صلاحية الجلسة. يُرجى تسجيل الدخول مجدّدًا.',
  },
  'errors.unauthorized': {
    en: 'Please sign in to continue.',
    ar: 'يُرجى تسجيل الدخول للمتابعة.',
  },
  'errors.forbidden': {
    en: "You don't have permission to do that.",
    ar: 'ليست لديك صلاحية لتنفيذ هذا الإجراء.',
  },
  'errors.notFound': {
    en: "The item you're looking for was not found.",
    ar: 'العنصر المطلوب غير موجود.',
  },
  'errors.validation': {
    en: 'Please review the highlighted fields and try again.',
    ar: 'يُرجى مراجعة الحقول المُظلَّلة والمحاولة مجدّدًا.',
  },
  'errors.conflict': {
    en: 'This action conflicts with the current state.',
    ar: 'يتعارض هذا الإجراء مع الحالة الراهنة.',
  },
  'errors.tooMany': {
    en: 'Too many requests. Please slow down and try again.',
    ar: 'عدد المحاولات كبير جدًا. يُرجى الانتظار قليلًا ثم المحاولة.',
  },
  'errors.server': {
    en: 'Something went wrong on our side. Please try again shortly.',
    ar: 'حدث خطأ من جانبنا. يُرجى المحاولة بعد قليل.',
  },
  'errors.unknown': {
    en: 'An unexpected error occurred. Please try again.',
    ar: 'حدث خطأ غير متوقّع. يُرجى المحاولة مجدّدًا.',
  },
  'errors.vouchersDisabled': {
    en: 'Vouchers will be available once subscriptions launch.',
    ar: 'ستكون كوبونات الخصم متاحة فور إطلاق الاشتراكات.',
  },
  'errors.paymentsDisabled': {
    en: 'Payments are not currently enabled.',
    ar: 'خدمة الدفع غير مُفعَّلة حاليًا.',
  },
  'errors.voucherPlanMismatch': {
    en: 'This voucher does not apply to the selected plan.',
    ar: 'هذا الكوبون لا ينطبق على الخطة المحددة.',
  },
  'errors.writerSubscriptionRequired': {
    en: 'Publishing tools require an active writer subscription. Please choose a writer plan to continue.',
    ar: 'أدوات النشر تتطلب اشتراك كاتب نشط. يرجى اختيار خطة كاتب للمتابعة.',
  },
  'errors.verifyEmailBeforeSubscribe': {
    en: 'Please verify your email first, then try subscribing again.',
    ar: 'يرجى تأكيد بريدك الإلكتروني أولاً ثم إعادة محاولة الاشتراك.',
  },
  'errors.planNotAvailable': {
    en: 'This plan is unavailable right now. Please choose another plan.',
    ar: 'هذه الخطة غير متاحة حالياً. يرجى اختيار خطة أخرى.',
  },
  'errors.alreadySubscribedSameType': {
    en: 'You already have a subscription in this plan type. Manage it from Subscriptions & Plans.',
    ar: 'لديك اشتراك بالفعل في نفس نوع الخطة. يمكنك إدارته من تبويب الاشتراكات والخطط.',
  },
  'errors.writerBlocksNewSubscription': {
    en: 'You already have an active writer subscription. You cannot start another subscription.',
    ar: 'لديك اشتراك كاتب نشط. لا يمكنك بدء اشتراك جديد.',
  },
  'errors.readerOnlyWriterSubscription': {
    en: 'You already have a reader subscription. You can only subscribe to a writer plan.',
    ar: 'لديك اشتراك قارئ. يمكنك الاشتراك في خطة كاتب فقط.',
  },
  'errors.resolvePendingAttempt': {
    en: 'You have an unfinished payment attempt for this plan type. Complete it or cancel it before starting a new one.',
    ar: 'لديك محاولة دفع غير مكتملة لنفس نوع الخطة. أكملها أو ألغِها قبل بدء محاولة جديدة.',
  },
  'errors.requireFullVoucher': {
    en: 'Subscriptions currently require a 100% voucher. Apply a full-discount code, then try again.',
    ar: 'الاشتراك حالياً يتطلب قسيمة خصم 100%. طبّق كود خصم كامل ثم حاول مرة أخرى.',
  },
  'errors.subscriptionIdRequired': {
    en: 'Please select a subscription to cancel, then try again.',
    ar: 'يرجى اختيار الاشتراك الذي تريد إلغاءه ثم المحاولة مرة أخرى.',
  },
  'errors.subscriptionNotCancelable': {
    en: 'This subscription cannot be cancelled in its current state. Refresh and try again.',
    ar: 'لا يمكن إلغاء هذا الاشتراك في حالته الحالية. حدّث الصفحة ثم حاول مرة أخرى.',
  },
  'errors.reactivateUnavailable': {
    en: 'Reactivation is unavailable for this subscription now. Please subscribe again.',
    ar: 'إعادة التفعيل غير متاحة لهذا الاشتراك حالياً. يرجى الاشتراك من جديد.',
  },
  'errors.subscriptionNotFound': {
    en: 'The subscription was not found. Refresh your list and try again.',
    ar: 'لم يتم العثور على الاشتراك. حدّث القائمة ثم حاول مرة أخرى.',
  },
  'errors.subscriptionResumeNotAllowed': {
    en: 'This subscription cannot resume checkout now. Please subscribe again if needed.',
    ar: 'لا يمكن استكمال الدفع لهذا الاشتراك حالياً. يمكنك الاشتراك مجدداً عند الحاجة.',
  },
  'errors.resumePlanGone': {
    en: 'The selected plan is no longer available. Please choose another plan.',
    ar: 'الخطة المحددة لم تعد متاحة. يرجى اختيار خطة أخرى.',
  },
  'errors.resumeNoPendingPayment': {
    en: 'No pending payment was found for this subscription. Please subscribe again.',
    ar: 'لا توجد دفعة معلقة لهذا الاشتراك. يرجى الاشتراك من جديد.',
  },
  'errors.paymentNotFound': {
    en: 'Payment record was not found. Please refresh and try again.',
    ar: 'لم يتم العثور على سجل الدفع. حدّث الصفحة ثم حاول مرة أخرى.',
  },
  'errors.paymentIntentionMissing': {
    en: 'Payment session details are missing. Start checkout again and retry.',
    ar: 'تفاصيل جلسة الدفع غير متوفرة. ابدأ الدفع مرة أخرى ثم أعد المحاولة.',
  },
  'errors.paymentStatusUnavailable': {
    en: 'We could not confirm payment status right now. Please try again shortly.',
    ar: 'تعذر تأكيد حالة الدفع حالياً. يرجى المحاولة بعد قليل.',
  },
  'errors.searchQueryRequired': {
    en: 'Please enter a search term, then try again.',
    ar: 'يرجى إدخال كلمة بحث ثم المحاولة مرة أخرى.',
  },
  'errors.requestBodyRequired': {
    en: 'Required request data is missing. Please review your input and try again.',
    ar: 'البيانات المطلوبة غير مكتملة. يرجى مراجعة المدخلات ثم المحاولة مرة أخرى.',
  },
  'errors.fileRequired': {
    en: 'Please choose a file before uploading.',
    ar: 'يرجى اختيار ملف قبل الرفع.',
  },
  'errors.invalidImageType': {
    en: 'Invalid image type. Please upload JPG, PNG, or WebP only.',
    ar: 'نوع صورة غير مدعوم. يرجى رفع JPG أو PNG أو WebP فقط.',
  },
  'errors.invalidBookFileType': {
    en: 'Invalid file type. Please upload a PDF or EPUB file.',
    ar: 'نوع ملف غير مدعوم. يرجى رفع ملف PDF أو EPUB.',
  },
  'errors.fileTooLarge': {
    en: 'The file is too large. Please choose a smaller file.',
    ar: 'حجم الملف كبير جداً. يُرجى اختيار ملف أصغر.',
  },
  'errors.coverTooLarge': {
    en: 'Cover image must be 5 MB or smaller.',
    ar: 'يجب ألا يتجاوز حجم صورة الغلاف 5 ميجابايت.',
  },
  'errors.manuscriptTooLarge': {
    en: 'Book file must be 100 MB or smaller.',
    ar: 'يجب ألا يتجاوز حجم ملف الكتاب 100 ميجابايت.',
  },
  'errors.autorenewPaymobOnly': {
    en: 'Automatic renewals are handled by Paymob. If your subscription ended, please subscribe again.',
    ar: 'التجديد التلقائي يتم عبر Paymob. إذا انتهى اشتراكك، يرجى الاشتراك مرة أخرى.',
  },
  'errors.fileNotFound': {
    en: 'The requested file or book was not found.',
    ar: 'الملف أو الكتاب المطلوب غير موجود.',
  },
  'errors.contentUnavailable': {
    en: 'No content is available for this item.',
    ar: 'لا يوجد محتوى متاح لهذا العنصر.',
  },
  'errors.fileMissingOnServer': {
    en: 'The file is missing on the server. Please contact support.',
    ar: 'الملف غير موجود على الخادم. يُرجى التواصل مع الدعم.',
  },
  'errors.invalidFilePath': {
    en: 'The file path is invalid.',
    ar: 'مسار الملف غير صالح.',
  },
  'errors.sampleUnavailable': {
    en: 'A sample is not available for this book.',
    ar: 'لا تتوفر عينة لهذا الكتاب.',
  },
  'errors.invalidSamplePath': {
    en: 'The sample path for this book is invalid.',
    ar: 'مسار العينة لهذا الكتاب غير صالح.',
  },
  'errors.publishingInvalidCategory': {
    en: 'Please select a valid book category.',
    ar: 'يُرجى اختيار فئة كتاب صالحة.',
  },
  'errors.publishingMustAcceptTerms': {
    en: 'You must accept the publishing terms and conditions to submit.',
    ar: 'يجب الموافقة على شروط وأحكام النشر قبل الإرسال.',
  },
  'errors.publishingCoverRequired': {
    en: 'A book cover image is required.',
    ar: 'صورة غلاف الكتاب مطلوبة.',
  },
  'errors.publishingTitlesRequired': {
    en: 'Both Arabic and English titles are required.',
    ar: 'عنوانا العربية والإنجليزية مطلوبان.',
  },
  'errors.publishingShortDescriptionsRequired': {
    en: 'Both Arabic and English short descriptions are required.',
    ar: 'الوصفان المختصران بالعربية والإنجليزية مطلوبان.',
  },
  'errors.publishingFullDescriptionsRequired': {
    en: 'Both Arabic and English full descriptions are required.',
    ar: 'الوصفان الكاملان بالعربية والإنجليزية مطلوبان.',
  },
  'errors.publishingSubtitleRequired': {
    en: 'If you enter a subtitle, both Arabic and English subtitles are required.',
    ar: 'عند إدخال عنوان فرعي، يجب إدخاله بالعربية والإنجليزية معاً.',
  },
  'errors.publishingBookFileRequired': {
    en: 'A book file (PDF or EPUB) is required.',
    ar: 'ملف الكتاب (PDF أو EPUB) مطلوب.',
  },
  'errors.publishingSingleFileRequired': {
    en: 'Upload either a PDF or an EPUB, not both.',
    ar: 'ارفع ملف PDF أو EPUB فقط وليس كليهما.',
  },
  'errors.publishingTitleArTooLong': {
    en: 'Book title (Arabic) must be 500 characters or fewer.',
    ar: 'يجب ألا يتجاوز عنوان الكتاب (عربي) 500 حرفًا.',
  },
  'errors.publishingTitleEnTooLong': {
    en: 'Book title (English) must be 500 characters or fewer.',
    ar: 'يجب ألا يتجاوز عنوان الكتاب (إنجليزي) 500 حرفًا.',
  },
  'errors.publishingSubtitleArTooLong': {
    en: 'Subtitle (Arabic) must be 500 characters or fewer.',
    ar: 'يجب ألا يتجاوز العنوان الفرعي (عربي) 500 حرفًا.',
  },
  'errors.publishingSubtitleEnTooLong': {
    en: 'Subtitle (English) must be 500 characters or fewer.',
    ar: 'يجب ألا يتجاوز العنوان الفرعي (إنجليزي) 500 حرفًا.',
  },
  'errors.publishingShortDescriptionArTooLong': {
    en: 'Short description (Arabic) must be 4,000 characters or fewer.',
    ar: 'يجب ألا يتجاوز الوصف المختصر (عربي) 4000 حرفًا.',
  },
  'errors.publishingShortDescriptionEnTooLong': {
    en: 'Short description (English) must be 4,000 characters or fewer.',
    ar: 'يجب ألا يتجاوز الوصف المختصر (إنجليزي) 4000 حرفًا.',
  },
  'errors.publishingFullDescriptionArTooLong': {
    en: 'Full description (Arabic) must be 50,000 characters or fewer.',
    ar: 'يجب ألا يتجاوز الوصف الكامل (عربي) 50000 حرفًا.',
  },
  'errors.publishingFullDescriptionEnTooLong': {
    en: 'Full description (English) must be 50,000 characters or fewer.',
    ar: 'يجب ألا يتجاوز الوصف الكامل (إنجليزي) 50000 حرفًا.',
  },
  'errors.publishingKeywordsTooLong': {
    en: 'Keywords must be 1,000 characters or fewer.',
    ar: 'يجب ألا تتجاوز الكلمات المفتاحية 1000 حرفًا.',
  },
  'errors.publishingIsbnTooLong': {
    en: 'ISBN must be 20 characters or fewer.',
    ar: 'يجب ألا يتجاوز رقم ISBN 20 حرفًا.',
  },
  'errors.publishingContributorNameTooLong': {
    en: 'Contributor names must be 100 characters or fewer.',
    ar: 'يجب ألا تتجاوز أسماء المساهمين 100 حرفًا.',
  },
  'errors.publishingFieldTooLong': {
    en: 'One of the fields is too long. Please shorten your text and try again.',
    ar: 'أحد الحقول طويل جدًا. يُرجى اختصار النص والمحاولة مجددًا.',
  },
};

/** When the API returns a raw resx key (server localization miss), map it to a frontend errors.* message. */
const API_ERROR_KEY_TO_HTTP_KEY: Record<string, string> = {
  'subscriptions.require_full_voucher_when_payments_disabled': 'errors.requireFullVoucher',
  'subscriptions.plan_not_found': 'errors.planNotAvailable',
  'subscriptions.plan_not_active': 'errors.planNotAvailable',
  'subscriptions.verify_email_first': 'errors.verifyEmailBeforeSubscribe',
  'subscriptions.writer_active_block': 'errors.writerBlocksNewSubscription',
  'subscriptions.reader_only_writer_upgrade': 'errors.readerOnlyWriterSubscription',
  'subscriptions.already_active_same_type': 'errors.alreadySubscribedSameType',
  'subscriptions.subscription_id_required': 'errors.subscriptionIdRequired',
  'subscriptions.not_found_or_not_cancelable': 'errors.subscriptionNotCancelable',
  'subscriptions.no_reactivatable': 'errors.reactivateUnavailable',
  'subscriptions.autorenew_paymob_only': 'errors.autorenewPaymobOnly',
  'common.user_not_found': 'errors.notFound',
  'payments.not_found': 'errors.paymentNotFound',
  'payments.no_intention_found': 'errors.paymentIntentionMissing',
  'payments.status_not_available': 'errors.paymentStatusUnavailable',
  'payments.disabled_nothing_to_verify': 'errors.paymentsDisabled',
  'common.no_file_provided': 'errors.fileRequired',
  'files.images_only_jpg_png': 'errors.invalidImageType',
  'files.images_only_jpg_png_webp': 'errors.invalidImageType',
  'files.book_only_pdf_epub': 'errors.invalidBookFileType',
  'files.cover_too_large': 'errors.coverTooLarge',
  'files.manuscript_too_large': 'errors.manuscriptTooLarge',
  'files.book_not_found': 'errors.fileNotFound',
  'files.content_not_available': 'errors.contentUnavailable',
  'files.book_missing_server': 'errors.fileMissingOnServer',
  'files.book_path_invalid': 'errors.invalidFilePath',
  'files.sample_not_available': 'errors.sampleUnavailable',
  'files.sample_path_invalid': 'errors.invalidSamplePath',
  'notifications.request_body_required': 'errors.requestBodyRequired',
  'search.query_required': 'errors.searchQueryRequired',
  'publishing.invalid_category': 'errors.publishingInvalidCategory',
  'publishing.must_accept_terms': 'errors.publishingMustAcceptTerms',
  'publishing.cover_required': 'errors.publishingCoverRequired',
  'publishing.titles_bilingual_required': 'errors.publishingTitlesRequired',
  'publishing.short_descriptions_bilingual_required': 'errors.publishingShortDescriptionsRequired',
  'publishing.full_descriptions_bilingual_required': 'errors.publishingFullDescriptionsRequired',
  'publishing.subtitle_bilingual_required': 'errors.publishingSubtitleRequired',
  'publishing.book_file_required': 'errors.publishingBookFileRequired',
  'publishing.single_file_required': 'errors.publishingSingleFileRequired',
  'publishing.title_ar_too_long': 'errors.publishingTitleArTooLong',
  'publishing.title_en_too_long': 'errors.publishingTitleEnTooLong',
  'publishing.subtitle_ar_too_long': 'errors.publishingSubtitleArTooLong',
  'publishing.subtitle_en_too_long': 'errors.publishingSubtitleEnTooLong',
  'publishing.short_description_ar_too_long': 'errors.publishingShortDescriptionArTooLong',
  'publishing.short_description_en_too_long': 'errors.publishingShortDescriptionEnTooLong',
  'publishing.full_description_ar_too_long': 'errors.publishingFullDescriptionArTooLong',
  'publishing.full_description_en_too_long': 'errors.publishingFullDescriptionEnTooLong',
  'publishing.keywords_too_long': 'errors.publishingKeywordsTooLong',
  'publishing.isbn_too_long': 'errors.publishingIsbnTooLong',
  'publishing.contributor_name_too_long': 'errors.publishingContributorNameTooLong',
  'publishing.field_too_long': 'errors.publishingFieldTooLong',
};

function serverMessageEnglishToHttpKey(normalized: string): string | null {
  if (normalized === 'this voucher does not apply to the selected plan.') return 'errors.voucherPlanMismatch';
  if (normalized === 'writer subscription required.') return 'errors.writerSubscriptionRequired';
  if (normalized === 'user not authenticated.') return 'errors.unauthorized';
  if (normalized === 'please verify your email address before subscribing.') return 'errors.verifyEmailBeforeSubscribe';
  if (normalized === 'plan not found or inactive' || normalized === 'plan not found') return 'errors.planNotAvailable';
  if (normalized === 'you already have an active subscription of this plan type') return 'errors.alreadySubscribedSameType';
  if (normalized === 'you already have a writer subscription or an unfinished writer checkout. you cannot start another subscription.'
    || normalized === 'you already have an active writer subscription. you cannot start another subscription.')
    return 'errors.writerBlocksNewSubscription';
  if (normalized === 'you already have a reader subscription. you can only subscribe to a writer plan.')
    return 'errors.readerOnlyWriterSubscription';
  if (normalized.includes('complete or cancel your existing payment attempt for this plan type')) return 'errors.resolvePendingAttempt';
  if (normalized.includes('require a 100% promo code') || normalized.includes('require a 100% voucher')) return 'errors.requireFullVoucher';
  if (normalized === 'subscription id is required.') return 'errors.subscriptionIdRequired';
  if (normalized === 'subscription not found or not cancelable') return 'errors.subscriptionNotCancelable';
  if (normalized.includes('no reactivatable subscription found')) return 'errors.reactivateUnavailable';
  if (normalized === 'payments are not currently enabled.') return 'errors.paymentsDisabled';
  if (normalized === 'subscription not found.') return 'errors.subscriptionNotFound';
  if (normalized === 'this subscription cannot be resumed.') return 'errors.subscriptionResumeNotAllowed';
  if (normalized === 'plan is no longer available. please pick another plan.') return 'errors.resumePlanGone';
  if (normalized === 'no pending payment on this subscription. please subscribe again.') return 'errors.resumeNoPendingPayment';
  if (normalized === 'payment not found.') return 'errors.paymentNotFound';
  if (normalized === 'no payment intention found for verification.') return 'errors.paymentIntentionMissing';
  if (normalized === 'could not retrieve payment status. try again later.') return 'errors.paymentStatusUnavailable';
  if (normalized === 'search query is required.') return 'errors.searchQueryRequired';
  if (normalized === 'request body is required.') return 'errors.requestBodyRequired';
  if (normalized === 'no file provided.') return 'errors.fileRequired';
  if (normalized === 'only jpg and png images are allowed.'
    || normalized === 'only jpg, png, and webp images are allowed.'
    || normalized === 'only jpg, png and webp images are allowed.') return 'errors.invalidImageType';
  if (normalized === 'only pdf and epub files are allowed.') return 'errors.invalidBookFileType';
  return null;
}

/** Raw exception text leaked by API — never show to end users. */
export function looksLikeTechnicalServerMessage(message: string | undefined): boolean {
  if (!message?.trim()) return false;
  const m = message.trim();
  return (
    /^Type:\s/i.test(m) ||
    /^Inner\s*\(\d+\):/i.test(m) ||
    /\bException\b/i.test(m) ||
    /EntityFrameworkCore/i.test(m) ||
    /MySqlException/i.test(m) ||
    /RetryLimitExceeded/i.test(m) ||
    /DbUpdateException/i.test(m) ||
    /Data too long for column/i.test(m) ||
    /StackTrace/i.test(m) ||
    /\bat Microsoft\./i.test(m) ||
    /caching_sha2_password/i.test(m) ||
    /ServerRSAPublicKeyFile/i.test(m)
  );
}

/**
 * Turn an API `message` string (localized English, Arabic, or raw resx key) into a user-facing string in `lang`.
 */
export function resolveServerFailureToastMessage(serverMessage: string | undefined, lang: string): string {
  if (!serverMessage?.trim()) return httpErrorMessage('errors.unknown', lang);
  if (looksLikeTechnicalServerMessage(serverMessage)) return httpErrorMessage('errors.server', lang);
  const trimmed = serverMessage.trim();
  const mappedKey = API_ERROR_KEY_TO_HTTP_KEY[trimmed];
  if (mappedKey) return httpErrorMessage(mappedKey, lang);
  const fromEnglish = serverMessageEnglishToHttpKey(trimmed.toLowerCase());
  if (fromEnglish) return httpErrorMessage(fromEnglish, lang);
  return trimmed;
}

export function httpErrorMessage(key: string, lang: string): string {
  const row = MESSAGES[key] ?? MESSAGES['errors.unknown'];
  return lang === 'ar' ? row.ar : row.en;
}
